package com.kinoshazam.screens.profile.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.CornerSize
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Scaffold
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import com.kinoshazam.changeLanguage
import com.kinoshazam.model.AppLanguage
import com.kinoshazam.model.ThemeState
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.screens.profile.ProfileUpdateStrip
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Small
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.uiKit.theme.uiKitLocalizationState
import com.kinoshazam.uiKit.theme.uiKitThemeState
import com.kinoshazam.view.OnBackPressed
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.app_version
import kinoshazam.resources.generated.resources.fantastic
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.logout
import kinoshazam.resources.generated.resources.profile_language
import kinoshazam.resources.generated.resources.profile_login_button
import kinoshazam.resources.generated.resources.profile_logout
import kinoshazam.resources.generated.resources.profile_subscribe
import kinoshazam.resources.generated.resources.profile_subscription
import kinoshazam.resources.generated.resources.profile_theme
import kinoshazam.resources.generated.resources.profile_theme_auto
import kinoshazam.resources.generated.resources.profile_theme_dark
import kinoshazam.resources.generated.resources.profile_theme_light
import kinoshazam.resources.generated.resources.profile_title
import kinoshazam.resources.generated.resources.watch_history
import models.User
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.profile.ProfileMVIAction
import presentation.screens.profile.ProfileState

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MediumProfileContent(
    state: ProfileState,
    modifier: Modifier = Modifier,
    onAction: (ProfileMVIAction) -> Unit,
) {

    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = false)
    LaunchedEffect(sheetState.currentValue) {
        if (sheetState.isVisible.not()) {
            onAction(ProfileMVIAction.CloseMenu)
        }
    }

    // Use bottomSheetState: CloseMenu can leave sheetState.isVisible true without hide().
    OnBackPressed(enabled = state.bottomSheetState != ProfileState.BottomSheetState.Closed) {
        onAction(ProfileMVIAction.CloseMenu)
    }

    Scaffold(
        modifier = modifier
            .fillMaxSize()
           .background(UiKitTheme.colors.transparent),
        containerColor = UiKitTheme.colors.transparent,
        content = { paddingValues ->
            DialogContent(
                onClose = {
                    onAction(ProfileMVIAction.BackPressed)
                }
            ) {
                when {
                    state.isLoading -> LoadingScreen()
                    state.isError -> ErrorScreen(onAction)
                    else -> ContentScreen(paddingValues, state, onAction)
                }
            }
        }
    )

    if (state.bottomSheetState != ProfileState.BottomSheetState.Closed) {
        ModalBottomSheet(
            sheetState = sheetState,
            containerColor = UiKitTheme.colors.color_4,
            shape = MaterialTheme.shapes.medium.copy(CornerSize(16.dp)),
            dragHandle = null,
            onDismissRequest = { onAction(ProfileMVIAction.CloseMenu) },
        ) {
            when (state.bottomSheetState) {
                is ProfileState.BottomSheetState.LanguageMenu -> LanguageMenuContent(onAction)
                is ProfileState.BottomSheetState.ThemeMenu -> ThemeMenuContent(onAction)
                is ProfileState.BottomSheetState.Closed -> Unit
            }
        }
    }
}


@Composable
private fun LoadingScreen() {
    Box(
        modifier = Modifier,
        contentAlignment = Alignment.Center,
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
    }
}

@Composable
private fun ErrorScreen(
    onAction: (ProfileMVIAction) -> Unit,
) {
//    ErrorView(
//        textError = stringResource(Res.string.error),
//        textAction = stringResource(Res.string.repeat),
//        onAction = { onAction(DetailsMVIAction.Reload) }
//    )
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    state: ProfileState,
    onAction: (ProfileMVIAction) -> Unit,
) {
    Column(
        modifier = Modifier
            .padding(paddingValues)
            .padding(top = 48.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {

        if (state.features.registration) {
            ProfileContent(state.user, onAction)
        }

        if (state.updateAvailable) {
            ProfileUpdateStrip(
                versionName = state.updateVersionName,
                onClick = { onAction(ProfileMVIAction.UpdateClicked) },
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
            )
        }

        if (state.user != null && state.features.subscription) {
            Button(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
                    .height(48.dp),
                shape = RoundedCornerShape(6.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = UiKitTheme.colors.color_4
                ),
                onClick = {
                }
            ) {
                TextLabel(
                    state = TextLabelState(
                        text = stringResource(Res.string.profile_subscription),
                        color = UiKitTheme.colors.color_2,
                    )
                )

                Spacer(modifier = Modifier.weight(1f))

                TextLabel(
                    state = TextLabelState(
                        text = stringResource(Res.string.profile_subscribe),
                        color = UiKitTheme.colors.color_1,
                    )
                )
            }
        }

        if (state.user != null && state.features.watchVideo) {
            Button(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
                    .height(48.dp),
                shape = RoundedCornerShape(6.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = UiKitTheme.colors.color_4
                ),
                onClick = {
                    onAction(ProfileMVIAction.OpenHistory)
                }
            ) {
                TextLabel(
                    state = TextLabelState(
                        text = stringResource(Res.string.watch_history),
                        color = UiKitTheme.colors.color_2,
                    )
                )
                Spacer(modifier = Modifier.weight(1f))
            }

            Spacer(modifier = Modifier.height(6.dp))
        }

        Button(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .height(48.dp),
            shape = RoundedCornerShape(6.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = UiKitTheme.colors.color_4
            ),
            onClick = {
                onAction(ProfileMVIAction.OpenLanguageMenu)
            }
        ) {
            TextLabel(
                state = TextLabelState(
                    text = stringResource(Res.string.profile_language),
                    color = UiKitTheme.colors.color_2,
                )
            )

            Spacer(modifier = Modifier.weight(1f))

            TextLabel(
                state = TextLabelState(
                    text = AppLanguage.fromCode(state.appLanguage.locale).lang,
                    color = UiKitTheme.colors.color_6,
                )
            )
        }

        Spacer(modifier = Modifier.height(6.dp))

        Button(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .height(48.dp),
            shape = RoundedCornerShape(6.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = UiKitTheme.colors.color_4
            ),
            onClick = {
                onAction(ProfileMVIAction.OpenThemeMenu)
            }
        ) {
            TextLabel(
                state = TextLabelState(
                    text = stringResource(Res.string.profile_theme),
                    color = UiKitTheme.colors.color_2,
                )
            )

            Spacer(modifier = Modifier.weight(1f))

            TextLabel(
                state = TextLabelState(
                    text = when (state.themeState) {
                        ThemeState.DARK -> stringResource(Res.string.profile_theme_dark)
                        ThemeState.LIGHT -> stringResource(Res.string.profile_theme_light)
                        ThemeState.SYSTEM -> stringResource(Res.string.profile_theme_auto)
                    },
                    color = UiKitTheme.colors.color_6,
                )
            )
        }

        TextLabel(
            modifier = Modifier
                .padding(top = 32.dp)
                .clickable { onAction(ProfileMVIAction.AppInfoClicked) },
            state = TextLabelState(
                text = "${stringResource(Res.string.app_version)}: ${com.kinoshazam.config.AppVersion.NAME}",
                textStyle = Small,
                color = UiKitTheme.colors.color_6,
            )
        )

        Spacer(modifier = Modifier.weight(1f))

        if (state.user != null) {
            Row(
                modifier = Modifier
                    .clickable {
                        onAction(ProfileMVIAction.LogoutClicked)
                    }
                    .padding(16.dp)
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(26.dp),
            ) {
                Image(
                    painter = painterResource(Res.drawable.logout),
                    colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                    contentDescription = null,
                )

                TextLabel(
                    state = TextLabelState(
                        text = stringResource(Res.string.profile_logout),
                        color = UiKitTheme.colors.color_2,
                    )
                )
            }
        }
    }
}

@Composable
private fun ProfileContent(
    user: User?,
    onAction: (ProfileMVIAction) -> Unit,
) {
    if (user == null) {
        Button(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .height(48.dp),
            shape = RoundedCornerShape(6.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = UiKitTheme.colors.color_1
            ),
            onClick = {
                onAction(ProfileMVIAction.LoginPressed)
            }
        ) {
            TextLabel(
                state = TextLabelState(
                    text = stringResource(Res.string.profile_login_button),
                    textStyle = BoldMedium,
                    color = UiKitTheme.colors.color_7,
                )
            )
        }
    } else {
        UiKitImage(
            modifier = Modifier
                .size(100.dp)
                .clip(CircleShape)
                .background(UiKitTheme.colors.color_4)
                .padding(4.dp),
            state = ImageUiKitState(
                url = user.avatar,
                contentDescription = null,
                placeholder = painterResource(Res.drawable.fantastic),
                placeholderColor = UiKitTheme.colors.color_1,
                contentScale = ContentScale.Crop,
            ),
        )
        if (user.userName.isNotEmpty()) {
            TextLabel(
                modifier = Modifier.padding(16.dp),
                state = TextLabelState(
                    text = user.userName,
                    textStyle = BoldMedium,
                    color = UiKitTheme.colors.color_2,
                )
            )
        } else {
            Spacer(modifier = Modifier.height(16.dp))
        }

        TextLabel(
            modifier = Modifier.padding(start = 16.dp, end = 16.dp, bottom = 32.dp),
            state = TextLabelState(
                text = user.email,
                color = UiKitTheme.colors.color_2,
            )
        )
    }
}

@Composable
private fun ThemeMenuContent(
    onAction: (ProfileMVIAction) -> Unit,
) {
    Column(
        modifier = Modifier.navigationBarsPadding(),
    ) {
        ThemeState.entries.forEach {
            TextLabel(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable {
                        onAction(ProfileMVIAction.ThemeSelected(it))
                        uiKitThemeState.value = it
                    }
                    .padding(vertical = 16.dp, horizontal = 16.dp),
                state = TextLabelState(
                    text = when (it) {
                        ThemeState.DARK -> stringResource(Res.string.profile_theme_dark)
                        ThemeState.LIGHT -> stringResource(Res.string.profile_theme_light)
                        ThemeState.SYSTEM -> stringResource(Res.string.profile_theme_auto)
                    },
                    color = UiKitTheme.colors.color_2,
                )
            )
        }
    }
}

@Composable
private fun LanguageMenuContent(
    onAction: (ProfileMVIAction) -> Unit,
) {
    Column(
        modifier = Modifier.navigationBarsPadding()
    ) {
        AppLanguage.entries.forEach {
            TextLabel(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable {
                        onAction(ProfileMVIAction.LanguageChanged(it))
                        changeLanguage(AppLanguage.fromCode(it.locale))
                        uiKitLocalizationState.value = it
                    }
                    .padding(vertical = 16.dp, horizontal = 16.dp),
                state = TextLabelState(
                    text = it.lang,
                    color = UiKitTheme.colors.color_2,
                )
            )
        }
    }
}
